import { createFileRoute } from "@tanstack/react-router";
import { CalendarClock } from "lucide-react";
import type { ReactNode } from "react";
import { apiQuery, useApiSuspenseQuery } from "@/client/lib/api";
import { ensurePage, pageHead } from "@/client/lib/page";
import { ConfirmClient } from "@/client/modules/confirmar/token/confirm-client";
import { CLINIC_INFO } from "@/shared/clinic-config";

/** Confirmação de consulta por link (pública; o paciente responde sem login). */
export const Route = createFileRoute("/confirmar/$token")({
  loader: ({ context, params }) =>
    ensurePage(context.queryClient, apiQuery("confirmar", "page", { token: params.token })),
  head: () => ({
    ...pageHead("Confirmar consulta"),
    meta: [...pageHead("Confirmar consulta").meta, { name: "robots", content: "noindex" }],
  }),
  component: ConfirmarPage,
});

function Card({ children }: { children: ReactNode }) {
  return <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-card">{children}</div>;
}

function ConfirmarPage() {
  const { token } = Route.useParams();
  const { data: ctx } = useApiSuspenseQuery("confirmar", "page", { token });
  return (
    <main className="flex min-h-screen items-center justify-center bg-page-bg px-4 py-10">
      <div className="w-full max-w-md">
        <div className="mb-6 flex flex-col items-center gap-2 text-center">
          <img src="/logo-mark.svg" alt="" width={44} height={44} className="h-11 w-11 rounded-xl" />
          <p className="font-display text-lg text-slate-900">{CLINIC_INFO.name}</p>
        </div>
        {!ctx ? (
          <Card>
            <div className="flex flex-col items-center gap-3 py-4 text-center">
              <CalendarClock className="h-8 w-8 text-slate-300" />
              <h1 className="text-base font-semibold text-slate-800">Link inválido ou expirado</h1>
              <p className="text-sm text-slate-500">
                Não encontramos essa consulta. Se precisar, fale direto com a clínica pelo telefone {CLINIC_INFO.phone}.
              </p>
            </div>
          </Card>
        ) : ctx.past ? (
          <Card>
            <div className="flex flex-col items-center gap-3 py-4 text-center">
              <CalendarClock className="h-8 w-8 text-slate-300" />
              <h1 className="text-base font-semibold text-slate-800">Essa consulta já passou</h1>
              <p className="text-sm text-slate-500">
                Para marcar um novo horário, fale com a clínica pelo telefone {CLINIC_INFO.phone}.
              </p>
            </div>
          </Card>
        ) : (
          <Card>
            <ConfirmClient
              token={token}
              firstName={ctx.firstName}
              dateLabel={ctx.dateLabel}
              timeLabel={ctx.timeLabel}
              doctorName={ctx.doctorName}
              initial={ctx.confirmation}
            />
          </Card>
        )}
        <p className="mt-5 text-center text-[11px] text-slate-400">
          {CLINIC_INFO.name} · {CLINIC_INFO.city} — {CLINIC_INFO.state}
        </p>
      </div>
    </main>
  );
}
